import {
  ResponseTypeEnum,
  SectionTypeEnum,
  TemplateNodeSchema,
  TemplateNodeTypeEnum,
  TemplateSection,
} from '@ulysses-inc/harami_api_client'
import type { TemplateNodesDict } from 'src/exShared/types/types'
import { UUID } from 'src/exShared/util/uuid'
import { getCreateNodeId } from 'src/features/templateEdit/common/createNode'
import { insertSiblingNodeId } from 'src/features/templateEdit/common/insertSiblingNodeId'
import {
  buildNewQuestionNode,
  QUESTION_FROM_RESPONSE_TYPE,
} from './tableNodeBuilders'

/** `sectionType = TABLE` のセクションかどうか。 */
/** 見出し名の初期値。見出しを表示している表では、見出し名が空ならこの名前に戻す。 */
export const DEFAULT_ROW_HEADING_NAME = '見出し'

export const isTableSectionNode = (node: TemplateNodeSchema): boolean =>
  node.section?.sectionType === SectionTypeEnum.Table

/**
 * 指定ノード自身、またはその子孫に表セクションが含まれているかを判定する。
 * 従業員チェックの表示可否判定や、DnD 移動制限の判定に使用する。
 */
export const containsTableSection = (
  node: TemplateNodeSchema,
  allNodes: TemplateNodesDict,
): boolean => {
  if (isTableSectionNode(node)) return true
  for (const childId of node.nodes) {
    const child = allNodes[childId]
    if (child && containsTableSection(child, allNodes)) return true
  }
  return false
}

/**
 * 新規の表セクション。
 * 表セクション自身は表全体のコンテナであり、行(子セクション)を内側に持つ。
 */
export const createDefaultTableSection = (): TemplateSection => ({
  name: '',
  sectionType: SectionTypeEnum.Table,
  isRepeat: 0,
})

/** 表操作 action の payload。Redux reducer が store に merge する。 */
export type TableSectionUpdate = {
  templateNodes: TemplateNodesDict
  activeNodeId?: number
  lastAddedNodeId?: number | null
  /** ページ直下に追加した場合、更新後の active page の node id 配列 */
  activePageNodeIds?: number[]
}

/** 操作後に選択させたいノードが確定している表の更新(列/行/分岐の追加・削除など)。 */
export type TableSectionUpdateWithActiveNode = TableSectionUpdate & {
  activeNodeId: number
}

/** UI / action から渡される表追加パラメータ。 */
export type AddTableSectionParams = {
  templateNodes: TemplateNodesDict
  /** 挿入位置の基準（ボタンが出ている行）。基準が無いとき(空の親/空ページ)は null。 */
  targetNode: TemplateNodeSchema | null
  parentNode: TemplateNodeSchema | null
  position: 'top' | 'bottom'
  /** parentNode === null かつ targetNode !== null のとき必須。ページ直下の現在の node id 一覧 */
  pageNodeIds?: number[]
}

/**
 * 表セクションノードを生成する。
 * - 表セクションはデフォルトでテンプレート行を1つ持つ。
 * - テンプレート行はデフォルトで最初の列を1つ持つ。
 */
const createTableSectionNodes = (
  templateNodes: TemplateNodesDict,
): { templateNodes: TemplateNodesDict; tableId: number } => {
  const newTemplateNodes = { ...templateNodes }

  const tableId = getCreateNodeId(newTemplateNodes)
  newTemplateNodes[tableId] = {
    id: tableId,
    uuid: UUID(),
    type: TemplateNodeTypeEnum.Section,
    nodes: [],
    section: createDefaultTableSection(),
  }

  const repeatableRowId = getCreateNodeId(newTemplateNodes)
  newTemplateNodes[repeatableRowId] = {
    id: repeatableRowId,
    uuid: UUID(),
    type: TemplateNodeTypeEnum.Section,
    nodes: [],
    // テンプレート行の名前が見出し名になる。新しい表は見出し名を入れた状態で始める。
    section: {
      name: DEFAULT_ROW_HEADING_NAME,
      sectionType: SectionTypeEnum.TableRow,
      isRepeat: 1,
    },
  }

  // 列追加(addTableColumn)と同じ初期質問定義で生成する。
  // 素の { responseType: TEXT } だけだと responseTexts(subType) が欠け、
  // 記録アプリ側でひな形の読み込みに失敗する。
  const initialColumnId = getCreateNodeId(newTemplateNodes)
  newTemplateNodes[initialColumnId] = buildNewQuestionNode(
    initialColumnId,
    QUESTION_FROM_RESPONSE_TYPE[ResponseTypeEnum.TEXT],
  )

  // 表セクションのnodesにテンプレート行を追加
  newTemplateNodes[tableId] = {
    ...newTemplateNodes[tableId],
    nodes: [repeatableRowId],
  }

  // テンプレート行のnodesに最初の列を追加
  newTemplateNodes[repeatableRowId] = {
    ...newTemplateNodes[repeatableRowId],
    nodes: [initialColumnId],
  }

  return { templateNodes: newTemplateNodes, tableId }
}

/** 中身が空のページに、最初の表セクションを1つ追加する。 */
const buildNewTableSectionOnEmptyPage = (
  templateNodes: TemplateNodesDict,
): TableSectionUpdate => {
  const { templateNodes: nodes, tableId } =
    createTableSectionNodes(templateNodes)
  return {
    templateNodes: nodes,
    activeNodeId: tableId,
    lastAddedNodeId: tableId,
    activePageNodeIds: [tableId],
  }
}

/**
 * 表セクション + テンプレート行を生成し、親（ページ or セクション）へ挿入する。
 */
const buildNewTableSection = ({
  templateNodes,
  targetNode,
  parentNode,
  position,
  pageNodeIds,
}: AddTableSectionParams): TableSectionUpdate => {
  const { templateNodes: nodes, tableId } =
    createTableSectionNodes(templateNodes)

  if (parentNode === null) {
    const siblings = pageNodeIds ?? []
    return {
      templateNodes: nodes,
      activeNodeId: tableId,
      lastAddedNodeId: tableId,
      activePageNodeIds: targetNode
        ? insertSiblingNodeId(siblings, targetNode.id, tableId, position)
        : [...siblings, tableId],
    }
  }

  const parentId = parentNode.id
  const parent = templateNodes[parentId]
  if (!parent) {
    throw new Error(`親ノード ${parentId} が見つかりません`)
  }

  return {
    templateNodes: {
      ...nodes,
      [parentId]: {
        ...parent,
        nodes: targetNode
          ? insertSiblingNodeId(parent.nodes, targetNode.id, tableId, position)
          : [...parent.nodes, tableId],
      },
    },
    activeNodeId: tableId,
    lastAddedNodeId: tableId,
  }
}

/** 表セクション追加の更新 を組み立てる。 */
export const buildAddTableSection = (
  params: AddTableSectionParams,
): TableSectionUpdate => {
  // 空ページへの初回追加(ページ nodes を差し替え)は「親も基準ノードも無い」ときだけ。
  // targetNode === null だけだと空の条件分岐/セクション配下への追加でもページを上書きしてしまう。
  if (params.parentNode === null && params.targetNode === null) {
    return buildNewTableSectionOnEmptyPage(params.templateNodes)
  }
  return buildNewTableSection({
    templateNodes: params.templateNodes,
    targetNode: params.targetNode,
    parentNode: params.parentNode,
    position: params.position,
    pageNodeIds: params.pageNodeIds,
  })
}

/**
 * 表セクション 1 つあたりのノード数の上限。企業別の拡張はない。
 * BE(harami_api_server) の `table_section_validator.go` と同じ値を二重に持つので、変更するときは BE も同時に更新すること。
 */
export const TABLE_SECTION_MAX_NODE_COUNT = 1400

const countNodesInSubtree = (
  templateNodes: TemplateNodesDict,
  nodeIds: number[],
  predicate: (node: TemplateNodeSchema) => boolean,
): number => {
  let count = 0
  for (const nodeId of nodeIds) {
    const node = templateNodes[nodeId]
    if (!node) {
      continue
    }
    if (predicate(node)) {
      count += 1
    }
    count += countNodesInSubtree(templateNodes, node.nodes, predicate)
  }
  return count
}

const isQuestionOrLogicNode = (node: TemplateNodeSchema): boolean =>
  node.type === TemplateNodeTypeEnum.Question ||
  node.type === TemplateNodeTypeEnum.Logic

/**
 * 定義は全リポジトリ共通: 質問ノードと条件分岐(Logic)ノードの総数。
 * セクションノード（テンプレート行 / 固定行 / 追加行 自身）は数えない。
 */
export const countTableSectionNodes = (
  tableNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
): number =>
  countNodesInSubtree(templateNodes, tableNode.nodes, isQuestionOrLogicNode)
